import React from "react";
import { Tooltip } from "react-tooltip";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $insertNodes,
  $createParagraphNode,
  $createTextNode,
} from "lexical";
import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import { marked } from "marked";
import { normalizeLatexToDoubleDollar } from "../utils/fileHelpers";
import { toEmbedUrl } from "../utils/fileHelpers";
import { AssignmentTypeValue } from "../../../../../../../../utils/consts";

/**
 * Reusable UpdatePlugin component for inserting content into Lexical editor.
 *
 * @param {Object} props
 * @param {Object} props.chat - Chat object containing `answer` or content to insert.
 * @param {number} props.index - Index of chat (if part of a list).
 * @param {Array} [props.chatHist] - Optional chat history array.
 * @param {Function} [props.scrollToBottom] - Function to scroll editor to bottom.
 * @param {Function} [props.setActiveWorkboard] - Setter for active workboard tab.
 * @param {Function} [props.setActiveTab] - Setter for current tab.
 * @param {Object} [props.workboards] - Workboard options object.
 * @param {boolean} [props.isAssignment] - Whether current context is assignment.
 * @param {boolean} [props.isRubric] - Whether current context is rubric.
 * @param {Function} [props.addLineBreaksAroundImages] - Function to add <br> tags around <img>.
 * @param {Function} [props.formatLatexInHtmlTags] - Function to handle inline LaTeX.
 * @param {Function} [props.processLatex] - Function to handle block LaTeX.
 * @param {string} [props.tooltipContent] - Tooltip text.
 */
export default function UpdatePlugin({
  chat,
  index,
  chatHist = [],
  scrollToBottom = () => { },
  setActiveWorkboard = () => { },
  setActiveTab = () => { },
  workboards = {},
  isAssignment = false,
  isRubric = false,
  addLineBreaksAroundImages = (v) => v,
  formatLatexInHtmlTags = (v) => v,
  processLatex = (v) => v,
  tooltipContent = "Add to Workboard",
  getSavedYoutubeLinks,
  setGetSpecificAssignmentType,
  isThisMessageStreaming
}) {
  const [editor] = useLexicalComposerContext();
  const { answer } = chat;


  const files = chat?.file || [];

  const ytFile = files.find(
    (f) => f?.source === "yt_link" || f?.type === "yt_link"
  );

  const fileYtUrl = ytFile?.url || "";

  // resolve which URL to use based on context
  let rawYtUrl;

  if (isAssignment) {
    if (fileYtUrl) {
      // User uploaded a YouTube link
      rawYtUrl = fileYtUrl;
    }
    //  else if (files.length === 0) {
    //   // No files at all -> use saved YouTube link
    //   rawYtUrl = getSavedYoutubeLinks || "";
    // } 
    else {
      // Files exist, but none are YouTube links -> don't use saved link
      rawYtUrl = "";
    }
  } else if (!isRubric) {
    // lesson / rubric: original behavior unchanged
    rawYtUrl = fileYtUrl;
  }
  function update() {

    if (isThisMessageStreaming) return;
    // Switch tab/workboard if handlers exist
    if (setActiveWorkboard && workboards) {
      setActiveWorkboard(
        isAssignment
          ? workboards.ASSIGNMENT
          : isRubric
            ? workboards.RUBRIC
            : workboards.LESSON
      );
    }
    if (isAssignment) {
      const answerText = chatHist[index]?.answer || answer || "";
      const assignmentTypeMatch = answerText.match(
        /\*{0,2}\s*Assignment Type\s*:?\s*\*{0,2}\s*:?\s*(.+)/i
      );
      const specificAssignmentType = assignmentTypeMatch
        ? assignmentTypeMatch[1].trim()
        : "";

      const value = AssignmentTypeValue[specificAssignmentType] ?? null;
      // "FITB" -> 3, "Mixed Questions" -> 7, unknown -> null


      setGetSpecificAssignmentType(value)
    }
    setActiveTab("workboard");


    const iframe = rawYtUrl
      ? `<iframe width="560" height="315" src="${toEmbedUrl(rawYtUrl)}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`
      : "";

    editor.update(() => {
      const parser = new DOMParser();

      // ✅ Step 1: prepare content
      const addNewLine = addLineBreaksAroundImages(chatHist[index]?.answer || answer);



      const preprocessedText = normalizeLatexToDoubleDollar(addNewLine)

      let preprocessedContent = preprocessedText;


      const tableRegex =
        /(\|.*\|(?:\r?\n|\r)\|[-:\s|]+\|(?:[\s\S]*?))(?=\n\s*\n|$)/g;

      preprocessedContent = preprocessedContent.replace(
        tableRegex,
        (tableMarkdown) => {
          // Step 1: Replace entire $$...$$ blocks with opaque tokens BEFORE marked()
          // This prevents marked() from treating | inside LaTeX as column separators
          // and prevents | inside |D| from becoming <strong>D</strong>
          const mathStore = [];
          const protectedMarkdown = tableMarkdown.replace(
            /\$\$([\s\S]*?)\$\$/g,
            (_, latex) => {
              const idx = mathStore.length;
              mathStore.push(latex);
              return `XMATH${idx}XENDX`;
            }
          );

          // Step 2: marked() now sees no LaTeX — table parses correctly
          let tableHtml = marked(protectedMarkdown);

          // Step 3: Swap tokens back with math-node spans
          tableHtml = tableHtml.replace(/XMATH(\d+)XENDX/g, (_, idx) => {
            const latex = mathStore[parseInt(idx, 10)];
            const escapedLatex = latex
              .replace(/</g, "\\lt ")
              .replace(/>/g, "\\gt ");
            return `<span class="math-node">$$${escapedLatex}$$</span>`;
          });

          return tableHtml;
        }
      );



      const tableMathStore = [];

      preprocessedContent = preprocessedContent.replace(
        /<span class="math-node">([\s\S]*?)<\/span>/g,
        (match) => {
          const token = `__TABLE_MATH_${tableMathStore.length}__`;

          tableMathStore.push(match);

          return token;
        }
      );

      const sections = preprocessedContent?.split(/\$\$/) || [];



      // const parseSections = (content) => {
      //   if (!content) return [''];

      //   const sections = [];
      //   const doubleDollarRegex = /\$\$([\s\S]*?)\$\$/g; // matches ONLY $$...$$
      //   let lastIndex = 0;
      //   let match;

      //   while ((match = doubleDollarRegex.exec(content)) !== null) {
      //     // Even index → plain text (never math)
      //     sections.push(content.slice(lastIndex, match.index));
      //     // Odd index → math block (only $$...$$)
      //     sections.push(match[1]);
      //     lastIndex = match.index + match[0].length;
      //   }

      //   // Remaining trailing text (always even index)
      //   sections.push(content.slice(lastIndex));

      //   return sections;
      // };

      // const sections = parseSections(preprocessedContent);

      const processedSections = sections.map((section, index) => {
        if (index % 2 === 1) {
          if (!section || section.trim() === '') {
            return '';
          }
          const escapedSection = section
            .replace(/</g, ' \\lt ')   // Use \lt instead of < (valid LaTeX)
            .replace(/>/g, ' \\gt ');

          return `<p>$$${escapedSection}$$</p>`;
        }

        return marked(section);
      });

      let last_chatHist = processedSections.join('');

      last_chatHist = last_chatHist.replace(
        /__TABLE_MATH_(\d+)__/g,
        (_, index) => tableMathStore[Number(index)]
      );



      const finalAnswer = last_chatHist
        .replace("<STARTOFTURN>", "")
        .replace("<ENDOFTURN>", "");

      // Post-process to merge LaTeX spans that appear between paragraph tags into the paragraphs
      // This prevents Lexical from creating broken node structures
      let mergedContent = finalAnswer
        // Merge LaTeX spans between closing and opening paragraph tags
        .replace(/<\/p>\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*<p>/gi, (match, latexContent) => {
          // Merge LaTeX into the following paragraph (right after opening <p> tag)
          return `</p><p><span>${latexContent}</span>`;
        })
        // Merge LaTeX spans between closing paragraph and other block elements
        .replace(/<\/p>\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*(<h[1-6]|<\/h[1-6]|<ul|<\/ul|<ol|<\/ol)/gi, (match, latexContent, nextTag) => {
          // Create a paragraph for LaTeX before the block element
          return `<p><span>${latexContent}</span></p>${nextTag}`;
        })
        // Merge LaTeX spans after block elements and before paragraphs
        .replace(/(<\/h[1-6]|<\/ul|<\/ol)\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*<p>/gi, (match, closingTag, latexContent) => {
          // Create a paragraph for LaTeX after the block element
          return `${closingTag}<p><span>${latexContent}</span></p><p>`;
        })
        // Handle LaTeX spans between two closing paragraph tags
        .replace(/<\/p>\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*<\/p>/gi, (match, latexContent) => {
          // Create a single paragraph containing the LaTeX
          return `<p><span>${latexContent}</span></p>`;
        });


      const tableStore = [];

      mergedContent = mergedContent.replace(
        /<table[\s\S]*?<\/table>/gi,
        (table) => {
          const token = `__TABLE_${tableStore.length}__`;
          tableStore.push(table);
          return token;
        }
      );

      let processedContent = formatLatexInHtmlTags(mergedContent);
      // let processedContent = mergedContent;

      // restore tables exactly as they were
      processedContent = processedContent.replace(
        /__TABLE_(\d+)__/g,
        (_, index) => tableStore[Number(index)]
      );




      // processedContent = iframe + processedContent;

      // ✅ Step 3: convert HTML → DOM → Lexical nodes
      const dom = parser.parseFromString(processedContent, "text/html");

      let nodes = $generateNodesFromDOM(editor, dom);



      try {
        // 🔑 wipe existing workboard content FIRST
        const root = $getRoot();
        root.clear();

        $insertNodes(nodes);

        // re-serialize + reparse (your existing normalization pass)
        const htmlString = $generateHtmlFromNodes(editor, null);
        root.clear();
        const dom2 = parser.parseFromString(`${htmlString}<h1> </h1>`, "text/html");
        nodes = $generateNodesFromDOM(editor, dom2);
        $insertNodes(nodes);
      } catch (error) {
        console.error("UpdatePlugin error:", error);
        const root = $getRoot();
        root.clear(); // also clear in the fallback so old data doesn't survive
        const p = $createParagraphNode();
        p.append($createTextNode(answer));
        root.append(p);
      } finally {
        scrollToBottom();
      }
    });
  }

  return (
    <>
      <i
        style={{ cursor: isThisMessageStreaming ? 'not-allowed' : 'pointer' }}
        data-tooltip-id="update-plugin"
        onClick={update}
        className="fa-solid fa-arrow-right cursor-pointer"
      ></i>
      <Tooltip
        id="update-plugin"
        place="bottom"
        content={tooltipContent}
        type="light"
        effect="float"
      />
    </>
  );
}
